import type { LinearRGB } from '../../../../core/color';
import type { Landmark } from '../../../../data/types';
import type { MeshBuilder, VertexStyle } from '../../../mesh/meshBuilder';
import { SURFACE, type SurfaceKind } from '../../../mesh/attributes';
import { meshFrameOf } from '../planFrame';
import { oudeKerkFrame, planOudeKerk } from './oudeKerkPlan';
import type { OudeKerkBuild } from './oudeKerkParts';
import { buildOudeKerkTower } from './oudeKerkTower';
import { buildOudeKerkNave } from './oudeKerkNave';
import { buildOudeKerkChoir } from './oudeKerkChoir';
import type { TracerySink } from '../tracery/tracerySink';
import { LANDMARK_PATTERN, patterned } from '../surfacePatterns';

/**
 * The share of the tower exaggeration the painter gave the Oude Kerk: at 0.5 the painter view draws
 * the spire 39.0 px and the tower body 61.0 px high, as painted (39.5 px, frame 767.5-807, and about
 * 63 px over the hidden foot; docs/plans/landmarks.md, "Silhouettes"). And its part of the landmark
 * triangle budget.
 */
export const OUDE_KERK_STYLE = { share: 0.5, maxTriangles: 30_000 } as const;

export interface OudeKerkColors {
  readonly stone: LinearRGB;
  readonly slate: LinearRGB;
  readonly lead: LinearRGB;
  readonly timber: LinearRGB;
  readonly dark: LinearRGB;
  readonly glass: LinearRGB;
  /** The windows' Lede stone jambs and tracery (Janse 2004, sections 1.6 and 1.9). */
  readonly dressing: LinearRGB;
}

interface Ids {
  readonly edgeId: number;
  readonly seed: number;
}

function stylesOf(colors: OudeKerkColors, ids: Ids, tracery: TracerySink) {
  const base = { ...ids, strokeAngle: 0, extent: [10, 10] as const, exaggeration: { base: 0, share: OUDE_KERK_STYLE.share } };
  const style = (color: LinearRGB, surface: SurfaceKind): VertexStyle => ({ ...base, color, surface });
  const glass = style(colors.glass, SURFACE.linework);
  const leaded = patterned(glass, LANDMARK_PATTERN.glass);
  const glazing = { reveal: style(colors.dressing, SURFACE.stone), tracery: { sink: tracery, edgeId: ids.edgeId }, leaded };
  const roof = patterned(style(colors.slate, SURFACE.roofSlate), LANDMARK_PATTERN.slate);
  return {
    stone: { wall: style(colors.stone, SURFACE.stone), roof, opening: glass, glazing },
    lead: patterned(style(colors.lead, SURFACE.linework), LANDMARK_PATTERN.lead),
    timber: style(colors.timber, SURFACE.wood),
  };
}

/** The Oude Kerk as it stood in 1538, its windows' tracery to `tracery`; returns the triangles it added. */
export function buildOudeKerk(builder: MeshBuilder, landmark: Landmark, colors: OudeKerkColors, ids: Ids, tracery: TracerySink): number {
  const plan = planOudeKerk(oudeKerkFrame(landmark));
  const build: OudeKerkBuild = { builder, frame: meshFrameOf(plan.frame), plan, ...stylesOf(colors, ids, tracery) };
  const before = builder.triangleCount;
  buildOudeKerkTower(build);
  buildOudeKerkNave(build);
  buildOudeKerkChoir(build);
  return builder.triangleCount - before;
}
